import Immutable, { List, Map } from 'immutable';

import { construct, resolve, resolveAll } from '../../../src/helpers/LinkMapper';
import BaseStore from '../../../src/stores/BaseStore';
import { ActionTypes } from '../../../src/constants/AppConstants';
import trainTime from '../../../src/lib/TrainTime';
import CustomerDetails from '../records/CustomerDetails';
import Claim from '../records/Claim';
import Payment from '../records/Payment';
import UserPreferences from '../../../src/records/customer/UserPreferences';

function createClaim ({ smartcard } = {}) {
    const datetime = trainTime().startOf('day');
    const journeyDate = Claim.getMaxDate();
    return new Claim({
        journeyDate,
        journeyTime: journeyDate,
        arrivalTime: datetime.clone().add(1, 'hour'),
        smartcardNumber: smartcard ? smartcard.getCardNumber() : null
    });
}

const isClaim = (claim) => (subject) => claim.internalId === subject.internalId;

export class DelayRepayStore extends BaseStore {

    serialize () {
        const customerDetails = this.state.get('customerDetails')
            .set('preferences', new UserPreferences())
            .set('acceptedTAndCs', false);
        return this.state.merge({
            claimRequestId: null,
            payment: null,
            submitting: false,
            error: null,
            submittedClaims: [],
            recaptcha: null,
            customerDetails
        });
    }

    deserialize (serialized) {
        if (serialized) {
            return super.deserialize(
                construct(Map, serialized, {
                    customerDetails: resolve.as(CustomerDetails),
                    claims: resolveAll.as(Claim)
                })
            );
        } else {
            return new Map();
        }
    }

    getDefaults () {
        return new Map({
            claimRequestId: null,
            customerDetails: new CustomerDetails(),
            claims: List.of(createClaim()),
            payment: new Payment(),
            submitting: false,
            error: null,
            submittedClaims: new List(),
            recaptcha: null
        });
    }

    getInitialState () {
        return this.getDefaults().merge(super.getInitialState());
    }

    resetState () {
        return this.setState(this.getDefaults());
    }

    getClaimIndex (claim) {
        return this.state.get('claims').findIndex(isClaim(claim));
    }

    getUploadsInProgress () {
        return this.state.get('claims').filter((claim) => claim.isUploading()).size;
    }

    hasValidCustomerDetails () {
        const customerDetails = this.get('customerDetails');
        return customerDetails && customerDetails.isComplete();
    }

    hasValidClaims () {
        const claims = this.get('claims');
        return claims && claims.every((claim) => claim.isComplete());
    }

    hasValidPayment () {
        const payment = this.get('payment');
        return payment && payment.isComplete();
    }

    hasSubmittedClaims () {
        return !!this.get('submittedClaims') && !!this.get('submittedClaims').size;
    }

    handleUserAdded ({ user }) {
        const customerDetails = this.get('customerDetails');
        const updated = customerDetails.mergeUser(user);

        if (updated !== customerDetails) {
            this.setState(this.state.set('customerDetails', updated));
        }
    }

    handleSmartcard({ smartcard }) {
        const smartcardNumber = smartcard.getCardNumber();
        const claims = this.get('claims');
        const updated = claims.map((claim) => {
            if (!claim.smartcardNumber) {
                return claim.set('smartcardNumber', smartcardNumber);
            }
            return claim;
        });

        if (!Immutable.is(updated, claims)) {
            this.setState(this.state.set('claims', updated));
        }
    }

    handleUpdateCustomer ({ field, value }) {
        const originalCustomer = this.state.get('customerDetails');
        let updatedCustomer;

        if (Array.isArray(field)) {
            updatedCustomer = originalCustomer.setIn(field, value);
        } else {
            updatedCustomer = originalCustomer.set(field, value);
            if (updatedCustomer.contactName !== originalCustomer.contactName) {
                updatedCustomer = updatedCustomer.setIn(['address', 'contactName'], updatedCustomer.contactName);
            }
        }
        this.setState(this.state.set('customerDetails', updatedCustomer));
    }

    handleAgeToggled ({ isOver16, preferences }) {
        const updatedCustomer = this.state.get('customerDetails')
            .set('isOver16', isOver16)
            .set('preferences', preferences);
        this.setState(this.state.set('customerDetails', updatedCustomer));
    }

    handleAddClaim ({ smartcard }) {
        const claims = this.state.get('claims').push(createClaim({ smartcard }));
        this.setState(this.state.set('claims', claims));
    }

    handleUpdateClaim ({ claim, field, value }) {
        const index = this.getClaimIndex(claim);
        this.setState(this.state.setIn(['claims', index, field], value));
    }

    handleCloneClaim ({ target }) {
        const index = this.getClaimIndex(target);
        const source = this.state.get('claims').first().merge({
            internalId: target.internalId,
            xhr: undefined,
            file: undefined,
            imageId: undefined,
            uploadFailed: undefined
        });
        this.setState(this.state.setIn(['claims', index], target.merge(source)));
    }

    handleRemoveClaim ({ claim }) {
        const claims = this.state.get('claims').filterNot(isClaim(claim));
        this.setState(this.state.set('claims', claims));
    }

    handleFileSelected ({ claim, file, usePrevious }) {
        const index = this.getClaimIndex(claim);
        const updatedClaim = claim.merge({
            file: usePrevious ? null : file,
            previousUpload: !!usePrevious,
            imageId: null
        });
        this.setState(this.state.setIn(['claims', index], updatedClaim));
    }

    handleUploadStarted ({ claim, xhr }) {
        const index = this.getClaimIndex(claim);
        const updatedClaim = claim.merge({
            xhr,
            uploadFailed: false,
            imageId: null
        });
        this.setState(this.state.setIn(['claims', index], updatedClaim));
    }

    handleUploadComplete ({ claim, imageId }) {
        const index = this.getClaimIndex(claim);
        const updatedClaim = this.state.getIn(['claims', index]) // claim has been updated during upload
            .merge({
                imageId,
                xhr: null
            });
        this.setState(this.state.setIn(['claims', index], updatedClaim));
    }

    handleUploadFailed ({ claim }) {
        const index = this.getClaimIndex(claim);
        const updatedClaim = this.state.getIn(['claims', index]) // claim has been updated during upload
            .merge({
                xhr: null,
                uploadFailed: true
            });
        this.setState(this.state.setIn(['claims', index], updatedClaim));
    }

    handleUploadCancelled ({ claim }) {
        const index = this.getClaimIndex(claim);
        const updatedClaim = this.state.getIn(['claims', index]) // claim has been updated during upload
            .merge({
                xhr: null,
                file: null,
                imageId: null,
                uploadFailed: false
            });
        this.setState(this.state.setIn(['claims', index], updatedClaim));
    }

    handleUpdatePayment ({ field, value }) {
        this.setState(this.state.setIn(['payment', 'bacsValidated'], field === 'paymentMethod' ?
                                    false :
                                    this.state.getIn(['payment', 'bacsValidated']))
                                .setIn(['payment', field], value));
    }

    handleSubmissionStarted () {
        this.setState(this.state.merge({
            submitting: true,
            error: null
        }));
    }

    handleSubmissionFailed ({ error }) {
        this.setState(this.state.merge({
            submitting: false,
            error
        }));
    }

    handleSubmitted ({ claimRequestId }) {
        this.setState(this.state.set('claimRequestId', claimRequestId));
    }

    handleComplete () {
        this.setState(this.getDefaults().set('submittedClaims', this.state.get('claims')));
    }

    handleCaptcha ({ recaptcha }) {
        this.setState(this.state.set('recaptcha', recaptcha));
    }

    handlePaymentReset () {
        this.setState(this.state.merge({
            payment: new Payment(),
            claimRequestId: null,
            submitting: false,
            error: null,
            recaptcha: null
        }));
    }

    handleAddressUpdated ({ address }) {
        const updatedDetails = this.state.get('customerDetails')
                                         .set('address', address);
        this.setState(this.state.set('customerDetails', updatedDetails));
    }

    handleBacsValidated ({ bacsValidated, sortCode, accountNumber }) {
        const updatedPayment = this.state.get('payment')
            .set('bacsValidated', bacsValidated)
            .set('bacsSortCode', sortCode)
            .set('bacsAccountNumber', accountNumber);
        this.setState(this.state.set('payment', updatedPayment));
    }

    getActionHandlers () {
        return {
            [ActionTypes.DELAY_REPAY_CUSTOMER_UPDATED]: this.handleUpdateCustomer,
            [ActionTypes.DELAY_REPAY_AGE_TOGGLED]: this.handleAgeToggled,
            [ActionTypes.DELAY_REPAY_USER_ADDED]: this.handleUserAdded,
            [ActionTypes.DELAY_REPAY_SMARTCARD_ADDED]: this.handleSmartcard,
            [ActionTypes.DELAY_REPAY_CLAIM_ADDED]: this.handleAddClaim,
            [ActionTypes.DELAY_REPAY_CLAIM_CLONED]: this.handleCloneClaim,
            [ActionTypes.DELAY_REPAY_CLAIM_REMOVED]: this.handleRemoveClaim,
            [ActionTypes.DELAY_REPAY_CLAIM_UPDATED]: this.handleUpdateClaim,
            [ActionTypes.DELAY_REPAY_CLAIM_FILE_SELECTED]: this.handleFileSelected,
            [ActionTypes.DELAY_REPAY_UPLOAD_STARTING]: this.handleUploadStarted,
            [ActionTypes.DELAY_REPAY_UPLOAD_COMPLETE]: this.handleUploadComplete,
            [ActionTypes.DELAY_REPAY_UPLOAD_FAILED]: this.handleUploadFailed,
            [ActionTypes.DELAY_REPAY_UPLOAD_CANCELLED]: this.handleUploadCancelled,
            [ActionTypes.DELAY_REPAY_PAYMENT_UPDATED]: this.handleUpdatePayment,
            [ActionTypes.DELAY_REPAY_SUBMITTING]: this.handleSubmissionStarted,
            [ActionTypes.DELAY_REPAY_FAILED]: this.handleSubmissionFailed,
            [ActionTypes.DELAY_REPAY_SUBMITTED]: this.handleSubmitted,
            [ActionTypes.DELAY_REPAY_CAPTCHA_COMPLETE]: this.handleCaptcha,
            [ActionTypes.DELAY_REPAY_CAPTCHA_EXPIRED]: this.handleCaptcha,
            [ActionTypes.DELAY_REPAY_CAPTCHA_VALIDATED]: this.handleComplete,
            [ActionTypes.DELAY_REPAY_CAPTCHA_BYPASSED]: this.handleComplete,
            [ActionTypes.DELAY_REPAY_CAPTCHA_FAILED]: this.handleSubmissionFailed,
            [ActionTypes.DELAY_REPAY_PAYMENT_RESET]: this.handlePaymentReset,
            [ActionTypes.DELAY_REPAY_RESET]: this.resetState,
            [ActionTypes.LOGGED_OUT]: this.resetState,
            [ActionTypes.DELAY_REPAY_ADDRESS_UPDATED]: this.handleAddressUpdated,
            [ActionTypes.DELAY_REPAY_BACS_VALIDATED]: this.handleBacsValidated
        };
    }
}

const instance = new DelayRepayStore({
    storageKey: 'delay-repay',
    expires: 60 * 60
});

export default instance;
